import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  Avatar,
  FormGroup,
  FormControlLabel,
  Switch,
  Slider,
  Collapse,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
  PlayArrow as PlayIcon,
  Save as SaveIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { reasoningApi } from '../../services/api';
import ReasoningPlanDisplay from './ReasoningPlanDisplay';
import SaveFlowModal from './SaveFlowModal';
import SimilarFlowsSearch from './SimilarFlowsSearch';
import MyFlowsModal from './MyFlowsModal';

const InteractiveReasoningForm = ({ onError }) => {
  const theme = useTheme();
  
  // Form state
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Advanced settings
  const [useTemplates, setUseTemplates] = useState(true);
  const [maxSteps, setMaxSteps] = useState(6);
  const [includeWebSearch, setIncludeWebSearch] = useState(true);
  
  // Plan state
  const [currentPlan, setCurrentPlan] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Save flow state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showMyFlowsModal, setShowMyFlowsModal] = useState(false);

  const handleGeneratePlan = async () => {
    if (!query.trim()) {
      setError('Please enter a complex query to generate a reasoning plan.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const planData = await reasoningApi.generatePlan({
        query: query.trim(),
        use_templates: useTemplates,
        max_steps: maxSteps,
        include_web_search: includeWebSearch
      });

      setCurrentPlan(planData);
      setActiveStep(1); // Move to plan review step
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate reasoning plan. Please try again.';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExecutePlan = async () => {
    if (!currentPlan) return;

    setIsExecuting(true);
    setError(null);
    setActiveStep(2); // Move to execution step

    try {
      const executionData = await reasoningApi.executePlan({
        plan_id: currentPlan.plan_id,
        steps: currentPlan.steps
      });

      setExecutionStatus(executionData);
      
      // If execution is still running, start polling for updates
      if (executionData.status === 'running') {
        pollExecutionStatus(executionData.execution_id);
      }
      
    } catch (err) {
      const errorMessage = err.message || 'Failed to execute reasoning plan. Please try again.';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsExecuting(false);
    }
  };

  const pollExecutionStatus = async (executionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusData = await reasoningApi.getExecutionStatus(executionId);
        setExecutionStatus(statusData);

        // Stop polling if execution is complete
        if (statusData.status !== 'running') {
          clearInterval(pollInterval);
          setIsExecuting(false);
          if (statusData.status === 'completed') {
            setActiveStep(3); // Move to results step
          }
        }
      } catch (err) {
        console.error('Error polling execution status:', err);
        clearInterval(pollInterval);
        setIsExecuting(false);
      }
    }, 2000); // Poll every 2 seconds

    // Clean up interval after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setIsExecuting(false);
    }, 300000);
  };

  const handlePlanChange = (updatedPlan) => {
    setCurrentPlan(updatedPlan);
  };

  const handleStartOver = () => {
    setCurrentPlan(null);
    setExecutionStatus(null);
    setIsExecuting(false);
    setActiveStep(0);
    setQuery('');
    setError(null);
  };

  const handleSaveFlow = () => {
    setShowSaveModal(true);
  };

  const handleSaveFlowSuccess = (result) => {
    setShowSaveModal(false);
    // Could show a success message here
  };

  const handleLoadRecommendedFlow = (flow) => {
    // Load the recommended flow
    setCurrentPlan(flow.plan);
    setQuery(flow.original_query);
    setActiveStep(1); // Move to plan review step
    setError(null);
  };

  const handleLoadMyFlow = (flow) => {
    // Load flow from "My Flows"
    setCurrentPlan(flow.plan);
    setQuery(flow.original_query);
    setActiveStep(1); // Move to plan review step
    setError(null);
    setShowMyFlowsModal(false);
  };

  const renderQueryForm = () => (
    <Box>
      {/* Query Input */}
      <TextField
        fullWidth
        label="Enter your complex medical query"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        required
        multiline
        rows={4}
        placeholder="e.g., What are the latest evidence-based protocols for managing neonatal sepsis in preterm infants, considering antibiotic resistance patterns and long-term neurodevelopmental outcomes?"
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
          },
        }}
      />

      {/* Similar Flows Search */}
      <SimilarFlowsSearch
        query={query}
        onLoadFlow={handleLoadRecommendedFlow}
        disabled={loading}
      />

      {/* Advanced Settings */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<SettingsIcon />}
          endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 2 }}
        >
          Advanced Settings
        </Button>

        <Collapse in={showAdvanced}>
          <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={useTemplates}
                    onChange={(e) => setUseTemplates(e.target.checked)}
                  />
                }
                label="Use predefined templates for common query types"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={includeWebSearch}
                    onChange={(e) => setIncludeWebSearch(e.target.checked)}
                  />
                }
                label="Include web search for latest information"
              />
            </FormGroup>

            <Box sx={{ mt: 2 }}>
              <Typography gutterBottom>
                Maximum Steps: {maxSteps}
              </Typography>
              <Slider
                value={maxSteps}
                onChange={(e, newValue) => setMaxSteps(newValue)}
                min={3}
                max={10}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Generate Plan Button */}
      <Button
        variant="contained"
        size="large"
        startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
        onClick={handleGeneratePlan}
        disabled={loading || !query.trim()}
        sx={{ borderRadius: 3, py: 1.5 }}
        fullWidth
      >
        {loading ? 'Generating Plan...' : 'Generate Reasoning Plan'}
      </Button>
    </Box>
  );

  const steps = [
    'Enter Query',
    'Review & Modify Plan',
    'Execute Plan',
    'View Results'
  ];

  return (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        mb: 3,
        borderRadius: 3,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.5)' : 'rgba(248, 249, 250, 0.8)',
        backdropFilter: 'blur(8px)',
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative gradient bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              bgcolor: 'secondary.main',
              mr: 2,
              width: 48,
              height: 48,
              boxShadow: 2,
            }}
          >
            <PsychologyIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" component="h2" fontWeight="bold">
              Interactive Reasoning
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate, review, and execute step-by-step reasoning plans
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<FolderIcon />}
          onClick={() => setShowMyFlowsModal(true)}
          sx={{ borderRadius: 2 }}
        >
          My Flows
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Progress Stepper */}
      <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      {activeStep === 0 && renderQueryForm()}
      
      {activeStep >= 1 && currentPlan && (
        <Box>
          <ReasoningPlanDisplay
            plan={currentPlan}
            onPlanChange={handlePlanChange}
            onExecutePlan={handleExecutePlan}
            executionStatus={executionStatus}
            isExecuting={isExecuting}
            showExecutionControls={activeStep === 1}
          />
          
          {activeStep >= 1 && (
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handleStartOver}
                disabled={isExecuting}
              >
                Start Over
              </Button>

              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveFlow}
                disabled={isExecuting || !currentPlan}
                color="secondary"
              >
                Save Flow
              </Button>

              {activeStep === 1 && (
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={handleExecutePlan}
                  disabled={isExecuting || currentPlan.steps.length === 0}
                >
                  {isExecuting ? 'Executing...' : 'Execute Plan'}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Save Flow Modal */}
      <SaveFlowModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        plan={currentPlan}
        originalQuery={query}
        onSaveSuccess={handleSaveFlowSuccess}
      />

      {/* My Flows Modal */}
      <MyFlowsModal
        open={showMyFlowsModal}
        onClose={() => setShowMyFlowsModal(false)}
        onLoadFlow={handleLoadMyFlow}
      />
    </Paper>
  );
};

export default InteractiveReasoningForm;
